<script setup lang="ts">
import ExcelUpdateBadge from '@/components/badges/ExcelUpdateBadge.vue'
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import {
  EyeIcon,
  GlobeIcon,
  RefreshIcon,
  // FileExportIcon,
  // IndianRupeeIcon,
  ExternalIcon,
} from '@/icons'
// ✅ Import from your enhanced badgeUtils
import {
  getBadgeStatus,
  getWorkflowLabel
} from '@/utils/badgeUtils'
import router from '@/router'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import type { AxiosError } from 'axios'
// import StatsCards from '@/components/cards/StatsCards.vue'
import EnvelopeIcon from '@/icons/EnvelopeIcon.vue'
import MobileIcon from '@/icons/MobileIcon.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

// Define interface for completed tenders using existing columns
type CompletedTenderResponse = Tender & {
  assignedAgentName?: string
  assignedAgentEmail?: string
  assignedAgentPhone?: string
  assignedEngineerName?: string
  assignedToEngineerName?: string
  assignedEngineerEmail?: string
  assignedEngineerPhone?: string
  engineerCompletionDate?: string
  contractValue?: number
  winningAmount?: number
  engineerStatus?: string
  engineerNotes?: string
  clientFeedback?: string
  workflowStatus?: string
}

const tenders = ref<CompletedTenderResponse[]>([])
const searchFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

// Statistics
// const completedCount = computed(() => tenders.value.length)
const totalContractValue = computed(() =>
  tenders.value.reduce(
    (sum, tender) =>
      sum + (tender.contractValue || tender.winningAmount || tender.estimatedCost || 0),
    0,
  ),
)

// Format number for display
const formatNumber = (num: number | string): string => {
  // Convert to number if it's a string
  if (typeof num === 'string') {
    // Remove currency symbols and commas
    num = parseFloat(num.replace(/[₹,]/g, ''))
  }

  if (!num || isNaN(num)) return '0'
  if (num >= 10000000) return (num / 10000000).toFixed(2) + ' Cr'
  if (num >= 100000) return (num / 100000).toFixed(2) + ' L'
  if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  return num.toLocaleString('en-IN')
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Fetch completed tenders from API
const fetchCompletedTenders = async () => {
  try {
    loading.value = true
    error.value = null

    const response = await api.get('/api/tenders/completed')

    if (response.data.success) {
      tenders.value = response.data.tenders.map((tender: CompletedTenderResponse) => ({
        ...tender,
        assignedAgentName: tender.assignedAgentName || 'Not Assigned',
        assignedEngineerName:
          tender.assignedEngineerName || tender.assignedToEngineerName || 'Not Assigned',
        contractValue: tender.contractValue || tender.winningAmount || 0,
        engineerCompletionDate: tender.engineerCompletionDate || '',
        engineerStatus: tender.engineerStatus || 'completed',
      }))
      console.log(`✅ Loaded ${tenders.value.length} completed tenders`, tenders.value)
    } else {
      error.value = response.data.error || 'Failed to load completed tenders'
    }
  } catch (err: unknown) {
    console.error('Error fetching completed tenders:', err)
    error.value = getAxiosErrorMessage(err as AxiosError)
  } finally {
    loading.value = false
  }
}

// Filtered tenders based on search
const filteredTenders = computed(() => {
  if (!searchFilter.value) return tenders.value

  const searchTerm = searchFilter.value.toLowerCase()
  return tenders.value.filter(
    (tender) =>
      tender.t247Id?.toLowerCase().includes(searchTerm) ||
      tender.referenceNo?.toLowerCase().includes(searchTerm) ||
      tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
      tender.organization?.toLowerCase().includes(searchTerm) ||
      tender.location?.toLowerCase().includes(searchTerm) ||
      tender.assignedAgentName?.toLowerCase().includes(searchTerm) ||
      tender.assignedEngineerName?.toLowerCase().includes(searchTerm) ||
      '' ||
      tender.engineerNotes?.toLowerCase().includes(searchTerm) ||
      tender.clientFeedback?.toLowerCase().includes(searchTerm),
  )
})

const viewTenderDetails = (tender: Tender) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'completed-tenders' },
  })
}

// Clear filters
const clearFilters = () => {
  searchFilter.value = ''
}

// Refresh data
const refreshData = async () => {
  await fetchCompletedTenders()
}

// Export completed tenders
// const exportCompletedTenders = async () => {
//   try {
//     const response = await api.get('/api/tenders/completed/export', {
//       responseType: 'blob',
//     })

//     const url = window.URL.createObjectURL(new Blob([response.data]))
//     const link = document.createElement('a')
//     link.href = url
//     link.setAttribute('download', `completed-tenders-${new Date().toISOString().split('T')[0]}.csv`)
//     document.body.appendChild(link)
//     link.click()
//     link.remove()

//     alert('✅ Completed tenders exported successfully!')
//   } catch (err: unknown) {
//     console.error('Error exporting completed tenders:', err)
//     alert(`Failed to export: ${getAxiosErrorMessage(err as AxiosError)}`)
//   }
// }

// Tooltip management for description
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipTarget = ref<HTMLElement>(); // Changed from null to undefined
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('top');

// Tooltip handlers
const showTooltip = (event: MouseEvent, text: string) => {
  const target = event.currentTarget as HTMLElement;
  tooltipTarget.value = target; // This will be HTMLElement, not null
  tooltipText.value = text;
  tooltipPosition.value = 'top';
  tooltipVisible.value = true;
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipTarget.value = undefined; // Changed from null to undefined
  tooltipText.value = '';
};

// Lifecycle
onMounted(() => {
  fetchCompletedTenders()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Completed Tenders" />

    <!-- Stats Cards -->
    <!-- <div class="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <StatsCards title="Total Completed Tenders" :value="completedCount" :icon="EyeIcon" />
      <StatsCards
        title="Total Contract Value"
        :value="`₹${formatNumber(totalContractValue)}`"
        :icon="IndianRupeeIcon"
      />
    </div> -->

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ error }}</p>
          <button
            @click="refreshData"
            class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
          >
            Click here to retry
          </button>
        </div>
      </div>
    </div>

    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Completed Tenders Details"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex items-center justify-between gap-2">
          <!-- Search Input -->
          <TextInputDefault
            v-model="searchFilter"
            placeholder="Search completed tenders..."
            id="searchcompletedtenders"
            name="searchcompletedtenders"
            type="text"
            size="xss"
          />

          <!-- Action Buttons -->
          <div class="flex gap-2 ml-auto">
            <!-- Clear Filter Button -->
            <BaseButton
              v-if="searchFilter"
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
            />

            <!-- Export Button -->
            <!-- <BaseButton
              text="Export"
              size="xss"
              variant="outline"
              :icon="FileExportIcon"
              @click="exportCompletedTenders"
              :disabled="tenders.length === 0"
            /> -->

            <!-- Refresh Button -->
            <BaseButton
              size="xss"
              :icon="RefreshIcon"
              @click="refreshData"
              :loading="loading"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i
            class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
          ></i>
          Loading completed tender data...
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="w-full flex flex-col items-center text-center py-8 gap-3 text-red-500"
        >
          <p>{{ error }}</p>
          <BaseButton text="Retry" size="sm" @click="refreshData" class="w-30" />
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="tenders.length === 0"
          :icon="GlobeIcon"
          title="No completed tenders"
          description="No tenders have been marked as completed yet."
          hint="Mark tenders as completed from the Agent Tenders, Engineer Tenders, or Tender Details page"
        />

        <!-- No Results after Filtering -->
        <EmptyState
          v-else-if="filteredTenders.length === 0"
          :icon="GlobeIcon"
          title="No matching completed tenders"
          description="No completed tenders match your search criteria."
          action-text="Clear Filters"
          @action="clearFilters"
        />

        <!-- Tender Table -->
        <DataTable
          v-else-if="filteredTenders.length > 0"
          :value="filteredTenders"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="450px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          :pt="{
            table: { style: { borderCollapse: 'separate', borderSpacing: '0' } },
            thead: { style: { position: 'sticky', top: '0', zIndex: '20' } },
            tbody: { style: { position: 'relative', zIndex: '10' } },
          }"
        >
          <!-- Tender ID Column -->
          <Column
            sortable
            frozen
            field="referenceNo"
            header="Reference No"
            headerStyle="position: sticky; left: 48px; z-index: 30;"
            bodyStyle="position: sticky; left: 48px; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex flex-col max-w-[100px] text-xs">
                <a
                  :href="data.tenderBriefLink"
                  target="_blank"
                  class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                  v-if="data.tenderBriefLink"
                >
                  {{ data.referenceNo }}
                  <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                </a>
                <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
                <ExcelUpdateBadge :tender="data" />
              </div>
            </template>
          </Column>

          <!-- Completion Date Column -->
          <Column sortable field="engineerCompletionDate" header="Completion Date">
            <template #body="{ data }">
              <div class="flex flex-col text-xs">
                {{ formatDate(data.updatedAt || '') }}
              </div>
            </template>
          </Column>

          <!-- Status Column - Using getWorkflowLabel for display and getBadgeStatus for color -->
          <Column sortable field="workflowStatus" header="Status">
            <template #body="{ data }">
              <Badge
                :value="getWorkflowLabel(data.workflowStatus || 'completed')"
                :class="'p-badge-' + getBadgeStatus(data.workflowStatus || 'completed')"
              />
            </template>
          </Column>

          <!-- Type Column -->
          <Column sortable field="type" header="Type">
            <template #body="{ data }">
              <Badge
                :value="data.sheetType"
                :class="'p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())"
              />
            </template>
          </Column>

          <!-- Tender Brief Column -->
          <Column sortable field="tenderBrief" header="Description">
            <template #body="{ data }">
              <div
                class="line-clamp-2 text-xs cursor-help"
                @mouseenter="(event) => showTooltip(event, data.tenderBrief)"
                @mouseleave="hideTooltip"
              >
                {{ data.tenderBrief }}
              </div>
            </template>
          </Column>

          <!-- Organization Column -->
          <Column sortable field="organization" header="Organization">
            <template #body="{ data }">
              <div class="text-xs">{{ data.organization }}</div>
            </template>
          </Column>

          <!-- Location Column -->
          <Column sortable field="location" header="Location">
            <template #body="{ data }">
              <span class="text-xs">{{ data.location }}</span>
            </template>
          </Column>

          <!-- Assigned Agent Column -->
          <Column sortable field="assignedAgent" header="Assigned Agent">
            <template #body="{ data }">
              <div class="flex flex-col space-y-1">
                <!-- Agent Name -->
                <div class="flex items-center justify-between">
                  <span class="text-xs font-medium text-gray-800">
                    {{ data.assignedAgentName || 'Not Assigned' }}
                  </span>
                </div>

                <!-- Agent Contact Info -->
                <div
                  v-if="data.assignedAgentEmail || data.assignedAgentPhone"
                  class="space-y-1.5 text-xs"
                >
                  <!-- Email -->
                  <div
                    v-if="data.assignedAgentEmail"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <EnvelopeIcon class="w-4 h-4" />
                    <a
                      :href="`mailto:${data.assignedAgentEmail}`"
                      class="truncate hover:text-blue-600 hover:underline transition-colors"
                      :title="`Email: ${data.assignedAgentEmail}`"
                    >
                      {{ data.assignedAgentEmail }}
                    </a>
                  </div>

                  <!-- Phone -->
                  <div
                    v-if="data.assignedAgentPhone"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <MobileIcon class="w-4 h-4" />
                    <a
                      :href="`tel:${data.assignedAgentPhone}`"
                      class="truncate hover:text-green-600 hover:underline transition-colors"
                      :title="`Phone: ${data.assignedAgentPhone}`"
                    >
                      {{ data.assignedAgentPhone }}
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </Column>

          <!-- Assigned Engineer Column -->
          <Column sortable field="assignedEngineerName" header="Assigned Engineer">
            <template #body="{ data }">
              <div class="flex flex-col space-y-1">
                <!-- Engineer Name -->
                <div class="flex items-center justify-between">
                  <span class="text-xs font-medium">
                    {{ data.assignedEngineerName || data.assignedToEngineerName || 'Not Assigned' }}
                  </span>
                </div>

                <!-- Engineer Contact Info -->
                <div
                  v-if="data.assignedEngineerEmail || data.assignedEngineerPhone"
                  class="space-y-1.5 text-xs"
                >
                  <!-- Email -->
                  <div
                    v-if="data.assignedEngineerEmail"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <EnvelopeIcon class="w-4 h-4" />
                    <a
                      :href="`mailto:${data.assignedEngineerEmail}`"
                      class="truncate hover:text-blue-600 hover:underline transition-colors"
                      :title="`Email: ${data.assignedEngineerEmail}`"
                    >
                      {{ data.assignedEngineerEmail }}
                    </a>
                  </div>

                  <!-- Phone -->
                  <div
                    v-if="data.assignedEngineerPhone"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <MobileIcon class="w-4 h-4" />
                    <a
                      :href="`tel:${data.assignedEngineerPhone}`"
                      class="truncate hover:text-green-600 hover:underline transition-colors"
                      :title="`Phone: ${data.assignedEngineerPhone}`"
                    >
                      {{ data.assignedEngineerPhone }}
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </Column>

          <!-- Contract Value Column -->
          <Column sortable field="contractValue" header="Contract Value">
            <template #body="{ data }">
              <div class="text-xs font-medium">
                ₹{{ formatNumber(data.contractValue || data.winningAmount || 0) }}
              </div>
            </template>
          </Column>

          <!-- Action Column -->
          <Column
            frozen
            field="action"
            header="Actions"
            headerStyle="width: 100px; position: sticky; right: 0; z-index: 30;"
            bodyStyle="position: sticky; right: 0; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex gap-2">
                <!-- View button -->
                <BaseButton
                  size="xss"
                  :aria-label="`view ${data.t247Id} details`"
                  @click="viewTenderDetails(data)"
                  variant="secondary"
                  :icon="EyeIcon"
                  tooltip="View Tender Details"
                  tooltipPosition="bottom"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} of {{ tenders.length }} completed tenders
            <span v-if="searchFilter">(filtered)</span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span>Total contract value: ₹{{ formatNumber(totalContractValue) }}</span>
            <span class="ml-4">Last updated: {{ new Date().toLocaleTimeString() }}</span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>

<style scoped>
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}

.transition-colors {
  transition: color 0.2s ease;
}
</style>
